import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Card, Chip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { type FC, useState } from "react";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import type { UnderutilizedNurse } from "../hooks/useHiringData";
import { HiringShiftType, ROLE_COLORS, ROLE_GROUP_MATCHES, type RoleGroup, ShiftFilter } from "../types";
import { ShiftFilterChips } from "./ShiftFilterChips";

interface UnderutilizedRoleCardProps {
	role: string;
	underutilizedNurses: UnderutilizedNurse[];
	onAccept: (role: string, filter: ShiftFilter, nurses: UnderutilizedNurse[]) => void;
}

export const UnderutilizedRoleCard: FC<UnderutilizedRoleCardProps> = ({ role, underutilizedNurses, onAccept }) => {
	const [filter, setFilter] = useState(ShiftFilter.All);
	const [selectedIds, setSelectedIds] = useState<Set<number> | null>(null);
	const [accepted, setAccepted] = useState(false);

	const theme = useTheme();
	const formatPersonName = useFormatPersonName();
	// Hex fallback (not CSS var): downstream template literals append hex-alpha
	// suffixes like `${color}60` which is invalid CSS against a var(...).
	const color = ROLE_COLORS[role] ?? theme.palette.primary.main;
	const roleMatches = ROLE_GROUP_MATCHES[role as RoleGroup] ?? [];
	const allForRole = underutilizedNurses.filter((nurse) => roleMatches.some((r: string) => nurse.nurse.role.includes(r)));

	if (allForRole.length === 0) return null;

	const filtered =
		filter === ShiftFilter.All
			? allForRole
			: allForRole.filter((nurse) => nurse.shiftCategory === (filter === ShiftFilter.Day ? HiringShiftType.Day : HiringShiftType.Night));

	const selected = selectedIds ?? new Set(filtered.map((nurse) => nurse.nurse.id));
	const hasSelection = filtered.some((nurse) => selected.has(nurse.nurse.id));

	const toggleNurse = (nurseId: number) => {
		const base = selectedIds ?? new Set(filtered.map((nurse) => nurse.nurse.id));
		const next = new Set(base);
		if (next.has(nurseId)) next.delete(nurseId);
		else next.add(nurseId);
		setSelectedIds(next);
	};

	const handleFilterChange = (f: ShiftFilter) => {
		setFilter(f);
		setSelectedIds(null);
	};

	const handleAccept = () => {
		setAccepted(true);
		onAccept(
			role,
			filter,
			filtered.filter((nurse) => selected.has(nurse.nurse.id)),
		);
	};

	return (
		<Card
			sx={{
				p: 2,
				mb: 1.5,
				border: accepted ? `1px solid ${color}60` : "1px solid #E8E8F0",
				bgcolor: accepted ? `${color}08` : "white",
				boxShadow: "none",
				borderRadius: 1,
			}}
		>
			{/* Header */}
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					<Box sx={{ px: 1.5, py: 0.3, borderRadius: "6px", bgcolor: `${color}18`, border: `1px solid ${color}40` }}>
						<Typography sx={{ fontSize: "13px", fontWeight: 600, color }}>{role}</Typography>
					</Box>
					<Typography sx={{ fontSize: "14px", fontWeight: 600, color: "primary.dark" }}>
						{filtered.length} {role.toLowerCase()}s can take more shifts
					</Typography>
				</Box>
				{accepted && <CheckCircleIcon sx={{ color, fontSize: 22 }} />}
			</Box>

			{/* Nurse chips */}
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 1.5 }}>
				{filtered.map((nurse) => {
					const isSelected = selected.has(nurse.nurse.id);
					return (
						<Chip
							key={nurse.nurse.id}
							label={`${formatPersonName(nurse.nurse)} (${nurse.scheduledShifts}/${nurse.allowedShifts * 2} shifts)`}
							size="small"
							onClick={() => toggleNurse(nurse.nurse.id)}
							sx={{
								bgcolor: isSelected ? (theme) => alpha(theme.palette.primary.main, 0.08) : "#FAFAFA",
								color: isSelected ? "primary.dark" : "primary.light",
								fontSize: "12px",
								fontWeight: 500,
								textDecoration: isSelected ? "none" : "line-through",
								border: isSelected ? `1px solid ${color}40` : "1px solid #E8E8F0",
								cursor: "pointer",
								"& .MuiChip-label": { px: 1 },
								"&:hover": { bgcolor: isSelected ? "#EEF1FA" : "#F4F4F4" },
							}}
						/>
					);
				})}
			</Box>

			{/* Filter + action */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<ShiftFilterChips value={filter} onChange={handleFilterChange} color={color} />
				<SystemButton
					size="small"
					buttonVariant="primary"
					disabled={accepted || !hasSelection}
					onClick={handleAccept}
					sx={{
						ml: "auto",
						bgcolor: color,
						"&:hover": { bgcolor: color, opacity: 0.9 },
					}}
				>
					{accepted ? "Applied" : "Apply to Simulation"}
				</SystemButton>
			</Box>
		</Card>
	);
};
